import React from 'react';
import DataLoader from '../DataLoader/DataLoader';
import Debug from '../../helpers/Debug';
import {Html5Qrcode} from 'html5-qrcode';
import './QRScanner.css';

/**
 * @typedef {Object} QRScannerProps
 * @property {number} width - Width of the scanner.
 * @property {number} height - Height of the scanner.
 * @property {Array} cameras - List of available cameras in the device.
 * @property {function} onScan - Function that will be executed when a scan is performed.
 * @property {function} onError - Function that will be executed when an error occurred.
 */

export default class QRScanner extends React.Component {
    /**
     * @param {QRScannerProps} props
     */

    constructor(props) {
        super(props);
        this.state = {
            isLoaded: false,
            selectedCamera: undefined,
            showCameraSelector: false
        };
        this._scanner = undefined;
        this._isMounted = true;
    };

    changeState = (newState, callback) => {
        if (this._isMounted) {
            this.setState(newState, () => {
                if (callback) callback();
            });
        }
    };

    componentDidMount() {
        this._isMounted = true;
        this.initializeScanner();
    };

    componentWillUnmount() {
        this._isMounted = false;
        this.destroyScanner()
            .catch((error) => {
                Debug.printToLog('error', `QRScanner removal error: ${error}`);
            });
    };

    getScannerConfig = () => {
        const isBarcodeDetectorAvailable = 'BarcodeDetector' in window;
        const fps = isBarcodeDetectorAvailable ? 10 : 5;
        const config = {
            disableFlip: false,
            fps: fps,
            qrbox: {width: 300, height: 300},
            experimentalFeatures: {
                useBarCodeDetectorIfSupported: true
            },
            videoConstraints: {
                width: {min: 640, ideal: 1280},
                height: {min: 480, ideal: 1280},
                aspectRatio: 4 / 3,
                advanced: [{width: 1920, height: 1280}, {aspectRatio: 4 / 3}]
            }
        };
        if (this.state.selectedCamera) config.videoConstraints.deviceId = {exact: this.state.selectedCamera};
        else config.videoConstraints.facingMode = 'environment';
        return config;
    };

    getCameraConfig = () => {
        return {}; // Options here does not seem to be working, use videoContraints in getScannerConfig instead.
    };

    initializeScanner = () => {
        const scannerConfig = this.getScannerConfig();
        const cameraConfig = this.getCameraConfig();
        Debug.printToLog('info', '[Scanner]', scannerConfig);
        this._scanner = new Html5Qrcode('qr-scanner');
        this._scanner.start(cameraConfig, scannerConfig, this.handleScan)
            .then(() => {
                const track = this._scanner.getRunningTrackSettings();
                const deviceId = track.deviceId;
                this.changeState({isLoaded: true, selectedCamera: deviceId});
            })
            .catch((_error) => {
                this.changeState({isLoaded: true, selectedCamera: undefined}, () => {
                    if (this.props.onError) this.props.onError('scan', _error);
                });
            });
    };

    destroyScanner = () => {
        if (this._scanner && this.validScannerState()) {
            return this._scanner.stop()
                .then(() => {
                    this._scanner.clear();
                    this._scanner = undefined;
                });
        } else return Promise.resolve();
    };

    validScannerState = () => {
        return this._scanner && this._scanner.getState() > 1; // 0 = UNKNOWN, 1 = NOT_STARTED, 2 = SCANNING and 3 = PAUSED
    };

    handleScan = (result) => {
        if (this.props.onScan) this.props.onScan(result);
    };

    shouldShowCameraSelector = () => {
        const {cameras} = this.props;
        const {showCameraSelector} = this.state;
        return cameras && cameras.length > 0 && showCameraSelector;
    };

    onCameraChange = (event) => {
        const value = event.target.value;
        this.changeState({selectedCamera: value}, () => {
            this.initializeScanner();
        });
    };

    renderLoader = () => {
        return (
            <div className='qr-scanner_loader'>
                <DataLoader message='Iniciando conexión con la cámara...' loaderSize={80}/>
            </div>
        );
    };

    renderSelector = () => {
        return (
            <div className='qr-scanner_select-container'>
                <select id='qr_scanner_cameras' name='cameras' value={this.state.selectedCamera} onChange={this.onCameraChange}>
                    {this.props.cameras.map((camera, index) => {
                        return (
                            <option key={index} value={camera.id}>{camera.label}</option>
                        );
                    })}
                </select>
            </div>
        );
    };

    render() {
        return (
            <div className='qr-scanner_container'>
                <div id='qr-scanner'>
                </div>
                {!this.state.isLoaded && this.renderLoader()}
                {this.shouldShowCameraSelector() && this.renderSelector()}
            </div>
        );
    };
}